import {
  IAreasTransfers,
  ISharingBasisEnum,
} from "commonFnBcode/types/dmc.types";
import React from "react";
import {
  Table,
  Badge,
  Button,
  Card,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from "react-bootstrap";
import { areasTransferApi as api } from "../api/AreasTransfer.api";
import ActivityLoader from "activity/componenets/ActivityLoader";
import { activityPriorityApi } from "activity/sub_modules/priority/api/ActivityPriority.api";

interface AreaTransferTableProps {
  onDelete: (id: any) => void;
  onCopy?: (area: IAreasTransfers) => void;
}
interface IFilter {
  regex: string;
  cities: number[];
}

const AreaTransferTable: React.FC<AreaTransferTableProps> = ({
  onDelete,
  onCopy,
}) => {
  // ============================== fetch cities ================================
  const { data: citiesApi } = activityPriorityApi.useGetCitiesQuery({
    filterBase: "areaTransfers",
  });

  const cities = citiesApi?.result || [];
  //======================= fetch areas transfers ===============================
  const [filters, setFilters] = React.useState<IFilter>({
    regex: "",
    cities: [],
  });

  const { data, isFetching } = api.useFetchAreasTransferQuery(filters);

  //--------------------------------------- search filters ----------------------------------
  const [debounceRegex, setDebouncedRegex] = React.useState("");
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters((val) => ({ ...val, regex: debounceRegex }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [debounceRegex]);

  const areas = data?.result || [];
  // ==========================================================================

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getTransferBadge = (type: string): string => {
    switch (type) {
      case "Pickup":
        return "success";
      case "Drop":
        return "info";
      case "Pickup & Drop":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handleCityFunc = (cityId, opType: "add" | "sub") => {
    const isCityExsists = filters.cities.find((v) => v === cityId);
    const isAddType = opType === "add";

    if (isCityExsists && isAddType) return;

    setFilters((val) => ({
      ...val,
      cities: isAddType
        ? [...val.cities, cityId]
        : val.cities.filter((v) => v !== cityId),
    }));
  };

  const getSharingBadge = (basis: string): string => {
    return basis === "PVT" ? "primary" : "dark";
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white py-3">
        <h5 className="mb-0">
          Area Transfers{" "}
          <span className="fs-6">
            (Transfer marked with '
            <i className="fas fa-star text-danger" />' are Stand Alone
            Transfers)
          </span>
        </h5>
        <input
          type="text"
          className="mt-3 form-control"
          placeholder="Search..."
          value={debounceRegex}
          onChange={(e) => setDebouncedRegex(e.target.value)}
        />
        <section className="d-flex justify-content-start align-items-end gap-3">
          <Dropdown>
            <Dropdown.Toggle
              variant="success"
              className="btn-flat mt-3"
              id="dropdown-basic"
            >
              Add Cities
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {cities.map((e) => {
                return (
                  <Dropdown.Item
                    onClick={() => handleCityFunc(e.id, "add")}
                    key={e.id}
                  >
                    {e.city}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          <div className="d-flex gap-2 align-items-center">
            {filters.cities.map((cityId) => (
              <Badge
                onClick={() => handleCityFunc(cityId, "sub")}
                bg="danger"
                className="pointer"
              >
                {cities.find((v) => v.id === cityId)?.city}{" "}
                <i className="fas fa-times" />
              </Badge>
            ))}
          </div>
        </section>
      </Card.Header>
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th className="fw-semibold">ID</th>
              <th className="fw-semibold">From</th>
              <th className="fw-semibold">To</th>
              <th className="fw-semibold">Trasnfer & Sharing</th>
              <th className="fw-semibold">Amount</th>
              <th className="fw-semibold">Start Date</th>
              <th className="fw-semibold">End Date</th>
              <th className="fw-semibold text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <ActivityLoader />
            ) : areas?.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No area transfers found
                </td>
              </tr>
            ) : (
              areas?.map((area) => {
                return (
                  <tr key={area.id}>
                    <td>
                      {" "}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {area.description || "No description available"}
                          </Tooltip>
                        }
                      >
                        <i
                          className="fa fa-info-circle text-muted"
                          role="button"
                        ></i>
                      </OverlayTrigger>{" "}
                      {area.isStandAlone && (
                        <i className="fas fa-star me-1 text-danger" />
                      )}
                      {area.id}
                    </td>
                    <td>{area.from?.name || "N/A"}</td>
                    <td>{area.to?.name || "N/A"}</td>
                    <td>
                      <section>
                        <div className="d-flex gap-1">
                          <Badge bg={getTransferBadge(area.transferType)}>
                            {area.transferType}
                          </Badge>
                          <Badge bg={getSharingBadge(area.sharingBasis)}>
                            {area.sharingBasis}
                          </Badge>
                          {area.sharingBasis === ISharingBasisEnum.private && (
                            <Badge bg="success">
                              {area.minPax && area.maxPax
                                ? ` min: ${area.minPax}, max: ${area.maxPax}`
                                : ""}
                            </Badge>
                          )}
                        </div>
                        <h6 className="pt-2 fw-bold text-danger text-truncate">
                          {area.boundTourOption?.name}
                        </h6>
                      </section>
                    </td>
                    <td className="fw-semibold">
                      ${(area.amount || 0).toFixed(2)}
                    </td>
                    <td>
                      <div className="small">
                        <div>{formatDate(area.validFromDate as any)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        <div>{formatDate(area.validTillDate as any)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        {onCopy && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onCopy(area)}
                            className="btn-icon rounded-1"
                          >
                            <i className="fa fa-copy"></i>
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDelete(area)}
                          className="btn-flat"
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default AreaTransferTable;
